// Activity events emitted by content/bookmarks.
//
// Subject convention:
//   - bookmark.added / bookmark.removed → subjectKind='article' so a
//     downstream "top bookmarked articles" subscriber can group by
//     article without a join. The collectionId lives on the payload.
//   - bookmark-collection.* → subjectKind='bookmark-collection' (the
//     LinkableKind exported by the schema), subjectId=<collectionId>.
//
// Payload mask discipline: NEVER inline the personal `note` (free-form
// user text) on bookmark.added — subscribers re-resolve through the
// repo if they need it. The note can carry PII; the activity row is
// durable + fans out to many subscribers.
import { z } from 'zod';
import { BookmarkCollectionKind, BookmarkCollectionVisibility } from './collection.js';
export const BOOKMARK_ACTIVITY_KINDS = {
    CollectionCreated: 'bookmark-collection.created',
    CollectionUpdated: 'bookmark-collection.updated',
    CollectionDeleted: 'bookmark-collection.deleted',
    Added: 'bookmark.added',
    Removed: 'bookmark.removed',
    Updated: 'bookmark.updated',
};
/** Glob patterns matching every bookmark-emitted kind. Useful for
 *  subscribers wanting a coarse "tell me about anything bookmark-
 *  shaped" subscription (notifications digest, search reindex, future
 *  webhooks). Two are needed because bookmark events use TWO subject
 *  prefixes — `bookmark-collection.*` for collection lifecycle events
 *  (subjectKind=bookmark-collection) and `bookmark.*` for item
 *  add/remove/update (subjectKind=article). Subscribers usually want
 *  one or both, depending on what they index. */
export const BOOKMARK_COLLECTION_ACTIVITY_KIND_GLOB = 'bookmark-collection.*';
export const BOOKMARK_ITEM_ACTIVITY_KIND_GLOB = 'bookmark.*';
const Iso = z.string().datetime({ offset: true });
export const BookmarkCollectionCreatedEvent = z.object({
    type: z.literal('bookmark-collection.created'),
    collectionId: z.string().uuid(),
    userId: z.string().uuid(),
    kind: BookmarkCollectionKind,
    visibility: BookmarkCollectionVisibility,
    occurredAt: Iso,
});
export const BookmarkCollectionUpdatedEvent = z.object({
    type: z.literal('bookmark-collection.updated'),
    collectionId: z.string().uuid(),
    userId: z.string().uuid(),
    /** Names of fields that actually changed in this update. Empty array
     *  means a no-op PATCH (caller passed the same values back). */
    changedFields: z.array(z.string()),
    occurredAt: Iso,
});
export const BookmarkCollectionDeletedEvent = z.object({
    type: z.literal('bookmark-collection.deleted'),
    collectionId: z.string().uuid(),
    userId: z.string().uuid(),
    /** Number of bookmark rows the CASCADE removed. Useful for analytics
     *  and for an eventual "are you sure? this contains N items" UI. */
    removedItemCount: z.number().int().nonnegative(),
    occurredAt: Iso,
});
export const BookmarkAddedEvent = z.object({
    type: z.literal('bookmark.added'),
    articleId: z.string().uuid(),
    userId: z.string().uuid(),
    collectionId: z.string().uuid(),
    collectionKind: BookmarkCollectionKind,
    occurredAt: Iso,
});
export const BookmarkRemovedEvent = z.object({
    type: z.literal('bookmark.removed'),
    articleId: z.string().uuid(),
    userId: z.string().uuid(),
    collectionId: z.string().uuid(),
    collectionKind: BookmarkCollectionKind,
    occurredAt: Iso,
});
/** Edits to the personal note / tags on an existing bookmark. Distinct
 *  from add/remove — the bookmark stays on the article timeline. */
export const BookmarkUpdatedEvent = z.object({
    type: z.literal('bookmark.updated'),
    articleId: z.string().uuid(),
    userId: z.string().uuid(),
    collectionId: z.string().uuid(),
    /** Names of changed fields — `'note' | 'tags'`. */
    changedFields: z.array(z.string()),
    occurredAt: Iso,
});
//# sourceMappingURL=events.js.map